import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

const RuokuonuoYhomeProfile = () => {
  const person = {
    id: "ruokuonuo-yhome",
    name: "Ruokuonuo Rose Yhome",
    honorific: "Dr",
    title: "Research Director",
    affiliation: "The Highland Institute, Kohima, Nagaland",
    image: "https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/jixadx5n_Ruokuonuo%20Yhome.jpg",
    email: "rose@highlandinstitute.org",
    category: "Senior Staff"
  };

  const SectionDivider = () => (
    <div className="my-12 flex items-center justify-center">
      <div className="h-px w-24 bg-stone-300"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="ruokuonuo-yhome-profile">
      {/* Breadcrumb Navigation */}
      <div className="bg-stone-50 border-b border-stone-200">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center text-sm text-stone-600">
            <Link to="/" className="hover:text-green-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/people" className="hover:text-green-600 transition-colors">People</Link>
            <span className="mx-2">/</span>
            <span className="text-stone-900">{person.name}</span>
          </nav>
        </div>
      </div>

      {/* Identity Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16 border-b border-stone-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-lg overflow-hidden bg-stone-200 shadow-lg">
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium uppercase tracking-wider rounded-full">
                    {person.category}
                  </span>
                </div>
                <h1 className="text-4xl font-serif font-bold text-stone-900 mb-2">
                  <span className="text-stone-600">{person.honorific} </span>
                  {person.name}
                </h1>
                <p className="text-xl text-stone-700 font-medium mb-4">
                  {person.title}
                </p>
                <p className="text-stone-600 leading-relaxed">
                  {person.affiliation}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">

          {/* Intellectual Biography */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Intellectual Biography
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                Ruokuonuo Rose Yhome is an archaeologist specialising in biological archaeology and the study of past dietary practices. She was a Fulbright-Nehru Postdoctoral Fellow at the Ungar Lab, Fulbright College of Arts and Sciences, University of Arkansas. Her postdoctoral research examined dietary behaviours among Chalcolithic, Harappan, and Iron Age populations through the analysis of dental remains from archaeological sites in India.
              </p>
              <p className="text-stone-700 leading-relaxed">
                She completed her PhD in Archaeology at the Department of Ancient Indian History, Culture, and Archaeology, Deccan College Post Graduate and Research Institute, Pune. Her doctoral research focused on dental microwear analysis of human dentition recovered from ancestral sites in Nagaland, offering valuable insights into the dietary habits and food preparation techniques of ancient Naga populations.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Research Foci & Areas of Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Research Foci and Areas of Work
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-6">
                Dr Yhome's academic interests include bioarchaeology, the role of diet in human ecological adaptation, morphological and palaeopathological studies of human remains, and the cultural study of Indigenous communities.
              </p>
              <p className="text-stone-700 font-medium mb-4">Key areas of work include:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Dental texture microwear analysis of past Indian populations</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Indigenous oral history and knowledge</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Archaeology of Northeast India</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Bioarchaeology of Naga ancestral sites</span></li>
                <li className="flex items-start gap-3 text-stone-700"><span className="text-green-600 mt-1">•</span><span>Community archaeology</span></li>
              </ul>
            </div>
          </section>

          <SectionDivider />

          {/* Highland Institute Projects */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Highland Institute Projects
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                At The Highland Institute, Dr Yhome previously served as Programme Manager for the <strong>"Global Health Research Group in Surgical Technologies"</strong> project, funded by the UK's National Institute for Health Research and led by doctors, scholars, and global health professionals at the University of Leeds.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                She was also Research Associate and Programme Manager for the Arts and Humanities Research Council, UK-funded project <strong>"Participatory Arts for Health Improvement in North East India"</strong>, part of the global "Changing the Story" project led by the University of Leeds.
              </p>
              <p className="text-stone-700 leading-relaxed">
                In addition, Dr Yhome was Co-Principal Investigator for <strong>"Naga Women's Health Channel: Developing an Online Health Community among Young Women in Northeast India"</strong>, funded by the Scottish Funding Council in collaboration with The Highland Institute and the University of Edinburgh.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Selected Publications */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Selected Publications &amp; Knowledge Outputs
            </h2>
            <div className="prose prose-stone max-w-none">
              <ul className="space-y-4">
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2025</span> &mdash; 'Comparing dental microwear in the populations at an Iron Age (c. 4010 BP) and Early Historic (c. 2600 BP) site in South India' in <em>International Journal of Osteoarchaeology</em>, Vol. 35, Issue 5, pp. 375–382.
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2023</span> &mdash; 'An Experimental Case Study: Application of Dental Microwear Analysis (DMA) on Dental Remains from Naga Ancestral Sites (Nagaland)' in <em>Man and Environment</em>, Vol. 48, Issue 2, pp. 94–107.
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2020</span> &mdash; 'A Preliminary Report on Human Skeletal Remains from Potlap Longkhap (PTL)' in <em>Archaeology of Mimi Caves: Kiphire District, Nagaland</em>, Vol. 1, Heritage Publishing House, Dimapur.
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2018</span> &mdash; 'An Ethnographic Account of Body Tattoo Art in Hutsü Village, Nagaland' in <em>Heritage: Journal of Multidisciplinary Studies in Archaeology</em>, Vol. 6, pp. 945–953.
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2015</span> &mdash; 'An Overview on Development of Paleopathology in the Indian Sub-continent' in <em>Indian Journal of Physical Anthropology and Human Genetics</em>, Vol. 34, pp. 283–300.
                </li>
              </ul>
            </div>
          </section>

          <SectionDivider />

          {/* Public Engagement, Teaching & Community Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Public Engagement, Teaching &amp; Community Work
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed">
                Alongside her scholarly work, Rose has been involved in community archaeology at various Naga ancestral sites, conducting archaeological excavations with communities seeking to better understand the context of their material culture. She also contributes to The Highland Institute's summer school by mentoring young aspirants interested in exploring academic pathways.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Postgraduate Qualifications */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Postgraduate Qualifications
            </h2>
            <ul className="space-y-2">
              <li className="flex items-start gap-3 text-stone-700">
                <span className="text-green-600 mt-1">•</span>
                <span>MA in Ancient Indian History and Culture (AIHC) and Archaeology</span>
              </li>
              <li className="flex items-start gap-3 text-stone-700">
                <span className="text-green-600 mt-1">•</span>
                <span>PhD in Archaeology (Deccan College Post Graduate and Research Institute, Pune)</span>
              </li>
            </ul>
          </section>

          <SectionDivider />

          {/* Contact & Scholarly Infrastructure */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Contact &amp; Scholarly Infrastructure
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-stone-50 p-6 rounded-lg">
                <h3 className="font-semibold text-stone-900 mb-3">Institutional Affiliation</h3>
                <p className="text-stone-700 leading-relaxed text-sm">
                  The Highland Institute<br />
                  Kohima, Nagaland
                </p>
              </div>
              <div className="bg-stone-50 p-6 rounded-lg">
                <h3 className="font-semibold text-stone-900 mb-3">Email</h3>
                <div className="flex items-center gap-3 text-stone-700">
                  <Mail className="w-5 h-5 text-green-600" />
                  <a
                    href={`mailto:${person.email}`}
                    className="hover:text-green-600 transition-colors break-all"
                    data-testid="rose-email-link"
                  >
                    {person.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="font-semibold text-stone-900 mb-4">Professional Role</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Research Director, The Highland Institute</span>
                </li>
              </ul>
            </div>

            <div className="mt-8">
              <h3 className="font-semibold text-stone-900 mb-4">Scholarly Profiles</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <a
                    href="https://orcid.org/0000-0002-0426-7541"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-600 transition-colors"
                    data-testid="rose-orcid-link"
                  >
                    ORCID
                  </a>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <a
                    href="https://deccancollege.academia.edu/ruokuonuoyhome"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-600 transition-colors"
                    data-testid="rose-academia-link"
                  >
                    Academia.edu
                  </a>
                </li>
              </ul>
            </div>
          </section>

        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-stone-50 border-t border-stone-200 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-stone-500 font-serif italic">
              This profile forms part of The Highland Institute's living scholarly archive, documenting research trajectories, collaborative commitments, and the intellectual work shaping the Institute's wider academic community.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuokuonuoYhomeProfile;
